import { QuestionnaireResponse } from '@medplum/fhirtypes';

const QUESTIONNAIRE_RESPONSE_ITEM_PATH = /QuestionnaireResponse\.item\[(\d+)\](?:\.item\[(\d+)\])?/;

type OperationOutcomeLike = {
  outcome?: {
    issue?: Array<{
      details?: { text?: string };
      expression?: string[];
    }>;
  };
};

/**
 * Maps Medplum/FHIR OperationOutcome issues (with QuestionnaireResponse.item paths in `expression`)
 * to human-readable lines for alerts. Pairs each settled result with the response at the same index.
 */
export function collectQuestionnaireResponseUpdateFieldErrors(
  responses: QuestionnaireResponse[],
  results: PromiseSettledResult<QuestionnaireResponse>[]
): string[] {
  const fieldErrors: string[] = [];

  if (responses.length !== results.length) {
    return fieldErrors;
  }

  results.forEach((result, index) => {
    if (result.status !== 'rejected') {
      return;
    }

    const response = responses[index];
    const error = result.reason;
    const outcome = (error as OperationOutcomeLike | undefined)?.outcome;
    const issues = Array.isArray(outcome?.issue) ? outcome.issue : [];

    if (issues.length && Array.isArray(response.item)) {
      for (const issue of issues) {
        const detailText: string = issue?.details?.text ?? (error instanceof Error ? error.message : 'Unknown error');
        const expression: string | undefined = Array.isArray(issue?.expression) ? issue.expression[0] : undefined;

        if (!expression) {
          continue;
        }

        const match = expression.match(QUESTIONNAIRE_RESPONSE_ITEM_PATH);
        if (!match) {
          continue;
        }

        const sectionIndex = Number(match[1]);
        const itemIndex = match[2] !== undefined ? Number(match[2]) : undefined;

        const sectionItem = response.item?.[sectionIndex];
        const fieldItem = itemIndex !== undefined ? sectionItem?.item?.[itemIndex] : sectionItem;
        const fieldLabel = fieldItem?.text ?? fieldItem?.linkId;

        if (fieldLabel) {
          fieldErrors.push(`${fieldLabel}: ${detailText}`);
        }
      }
    } else {
      const fieldName = response?.item?.[0]?.text;
      fieldErrors.push(
        `${fieldName ?? 'Unknown field'}: ${
          error instanceof Error ? error.message : 'Unknown error updating questionnaire response'
        }`
      );
    }
  });

  return fieldErrors;
}
